import React, { useState } from 'react';
import { updateTableBd, deleteBd } from '../../config/functions';
import { showConfirmDialog } from '../utils/ConfirmDialog';

const ModalCommentDetail = ({ 
    show, 
    comment, 
    user,
    tipo,
    onClose, 
    onCommentUpdated,
    theme
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedComment, setEditedComment] = useState(comment?.comentario || '');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Aplicar tema por defecto si no se proporciona
    const currentTheme = theme || {
        bgColor: '#1a1a1a',
        fontColor: '#ffffff',
        chartBorder: '#FF6B35'
    };

    if (!show || !comment) return null;

    const handleEdit = () => {
        setEditedComment(comment.comentario);
        setIsEditing(true);
        setError('');
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedComment(comment.comentario);
        setError('');
    };

    const handleSaveEdit = async () => {
        if (!editedComment.trim()) {
            setError('El comentario no puede estar vacío');
            return;
        }

        try {
            setLoading(true);
            setError('');
            const fecha = new Date();

            const dataSend = {
                nb_com_idn: comment.nb_com_idn,
                nb_com_val: editedComment.trim(),
                vr_adt_usureg: user,
                dt_adt_fchreg: fecha.toISOString()
            };

            const response = await updateTableBd('editcomment',dataSend);
            
            if (response) {
                // Notificar al componente padre que el comentario fue actualizado
                if (onCommentUpdated) {
                    onCommentUpdated();
                }
                setIsEditing(false);
                onClose();
            }
        } catch (err) {
            console.error('Error editando comentario:', err);
            setError('Error al guardar el comentario');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        showConfirmDialog(
            '¿Estás seguro de que deseas eliminar este comentario? Esta acción no se puede deshacer.',
            async () => {
                try {
                    setLoading(true);
                    setError('');

                    const idnc = comment.nb_com_idn;
                    const response = await deleteBd(idnc, 'deletecomment');
                    
                    // Limpiar loading antes de cerrar
                    setLoading(false);
                    
                    // Cerrar modal inmediatamente
                    onClose();
                    
                    // Notificar al componente padre que el comentario fue eliminado
                    if (onCommentUpdated) {
                        await onCommentUpdated();
                    }
                } catch (err) {
                    console.error('Error eliminando comentario:', err);
                    setError('Error al eliminar el comentario');
                    setLoading(false);
                }
            }
        );
    };

    return (
        <div 
            className="modal-overlay"
            onClick={onClose}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                padding: '20px',
                backdropFilter: 'blur(4px)'
            }}
        >
            <div 
                className="comment-detail-modal modal-themed-content"
                onClick={(e) => e.stopPropagation()}
                style={{
                    borderRadius: '12px',
                    padding: '24px',
                    maxWidth: '600px',
                    width: '100%',
                    maxHeight: '80vh',
                    overflow: 'auto',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.5)',
                    position: 'relative'
                }}
            >
                {/* Botón Cerrar */}
                <button
                    onClick={onClose}
                    disabled={loading}
                    style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: '#FF6B35',
                        border: 'none',
                        fontSize: '20px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        color: '#fff',
                        padding: '4px 12px',
                        lineHeight: '1',
                        borderRadius: '4px',
                        fontWeight: 'bold',
                        opacity: loading ? 0.5 : 1
                    }}
                    title="Cerrar"
                >
                    ×
                </button>

                {/* Header con Variable */}
                <div style={{
                    borderBottom: '3px solid #FF6B35',
                    paddingBottom: '16px',
                    marginBottom: '20px'
                }}>
                    <div style={{
                        fontSize: '12px',
                        color: '#999',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: '8px'
                    }}>
                        📊 Variable
                    </div>
                    <div style={{
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: '#FF6B35'
                    }}>
                        {comment.varName}
                    </div>
                </div>

                {/* Comentario */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{
                        fontSize: '12px',
                        color: '#999',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: '8px'
                    }}>
                        💬 Comentario
                    </div>
                    
                    {isEditing ? (
                        <textarea
                            value={editedComment}
                            onChange={(e) => setEditedComment(e.target.value)}
                            disabled={loading}
                            style={{
                                width: '100%',
                                minHeight: '100px',
                                fontSize: '15px',
                                lineHeight: '1.6',
                                color: '#e0e0e0',
                                padding: '16px',
                                backgroundColor: '#2a2a2a',
                                borderRadius: '8px',
                                border: '2px solid #FF6B35',
                                resize: 'vertical',
                                fontFamily: 'inherit'
                            }}
                        />
                    ) : (
                        <div style={{
                            fontSize: '15px',
                            lineHeight: '1.6',
                            color: '#e0e0e0',
                            padding: '16px',
                            backgroundColor: '#2a2a2a',
                            borderRadius: '8px',
                            borderLeft: '4px solid #FF6B35',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word'
                        }}>
                            {comment.comentario}
                        </div>
                    )}
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{
                        backgroundColor: '#ff4444',
                        color: '#fff',
                        padding: '12px',
                        borderRadius: '6px',
                        marginBottom: '16px',
                        fontSize: '14px'
                    }}>
                        ⚠️ {error}
                    </div>
                )}

                {/* Botones de Acción */}
                {isEditing ? (
                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        marginBottom: '20px'
                    }}>
                        <button
                            onClick={handleSaveEdit}
                            disabled={loading}
                            style={{
                                flex: 1,
                                padding: '12px 24px',
                                backgroundColor: '#4CAF50',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                opacity: loading ? 0.6 : 1
                            }}
                        >
                            {loading ? '⏳ Guardando...' : '✅ Guardar'}
                        </button>
                        <button
                            onClick={handleCancelEdit}
                            disabled={loading}
                            style={{
                                flex: 1,
                                padding: '12px 24px',
                                backgroundColor: '#666',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                opacity: loading ? 0.6 : 1
                            }}
                        >
                            ❌ Cancelar
                        </button>
                    </div>
                ) : (
                    tipo === '1' && (
                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            marginBottom: '20px'
                        }}>
                            <button
                                onClick={handleEdit}
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    backgroundColor: '#2196F3',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                ✏️ Editar
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    padding: '12px 24px',
                                    backgroundColor: '#f44336',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    opacity: loading ? 0.6 : 1
                                }}
                            >
                                {loading ? '⏳ Eliminando...' : '🗑️ Eliminar'}
                            </button>
                        </div>
                    )
                )}

                {/* Footer con Usuario y Fecha */}
                <div style={{
                    borderTop: '1px solid #333',
                    paddingTop: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px'
                }}>
                    <div>
                        <div style={{
                            fontSize: '11px',
                            color: '#999',
                            marginBottom: '4px'
                        }}>
                            👤 Usuario
                        </div>
                        <div style={{
                            fontSize: '14px',
                            color: '#ccc',
                            fontWeight: '500'
                        }}>
                            {comment.usuario}
                        </div>
                    </div>
                    <div>
                        <div style={{
                            fontSize: '11px',
                            color: '#999',
                            marginBottom: '4px'
                        }}>
                            📅 Fecha
                        </div>
                        <div style={{
                            fontSize: '14px',
                            color: '#ccc',
                            fontWeight: '500'
                        }}>
                            {comment.fecha}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalCommentDetail;
